export const daysFromDay = (days: number, day = new Date()) => {
  const utcDay = day.getUTCDate();
  const utcMonth = day.getUTCMonth();
  const utcYear = day.getUTCFullYear();
  const daysFromNow = new Date(utcYear, utcMonth, utcDay + days);
  return daysFromNow;
};

export const secondsFromNow = (seconds: number, day = new Date()) =>
  new Date(day.getTime() + seconds * 1000);
export const minutesFromNow = (minutes: number, day = new Date()) =>
  secondsFromNow(minutes * 60, day);

export const formatWebsiteDate = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  return date.toLocaleDateString("en-US", options);
};

export const getMinutesHoursDateAndYearFromDateString = (
  dateString: Date | string,
) => {
  const date = new Date(dateString);
  const minutes = date.getMinutes();
  const hours = date.getHours();
  const day = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();
  // console.log({ minutes, hours, day, month, year });
  return { minutes, hours, day, month, year };
};

/**
 * Converts a date to a specific timezone without changing the displayed time
 * This is useful when a user selects a time in one timezone but wants it saved as that same time in another timezone
 */
export const convertToTargetTimezone = (
  dateTime: string | Date,
  targetTimezoneOffset: string,
): Date => {
  const date = typeof dateTime === "string" ? new Date(dateTime) : dateTime;

  // Parse the target timezone offset (e.g., "+01:00" to minutes)
  const targetOffsetSign = targetTimezoneOffset.charAt(0) === "-" ? -1 : 1;
  const targetOffsetHours = parseInt(targetTimezoneOffset.substring(1, 3), 10);
  const targetOffsetMinutes = parseInt(
    targetTimezoneOffset.substring(4, 6),
    10,
  );
  const targetOffsetInMinutes =
    targetOffsetSign * (targetOffsetHours * 60 + targetOffsetMinutes);

  // Get user's local timezone offset in minutes
  const localOffsetInMinutes = date.getTimezoneOffset() * -1;

  // Calculate the difference between target and local timezone
  const offsetDifferenceInMinutes =
    targetOffsetInMinutes - localOffsetInMinutes;

  // Create a new date adjusted by the offset difference
  const adjustedTime = new Date(
    date.getTime() - offsetDifferenceInMinutes * 60 * 1000,
  );

  return adjustedTime;
};

/**
 * Format a date with timezone information for display
 */
export const formatDateWithTimezone = (
  date: Date,
  timezone: { name: string; offset: string },
) => {
  if (!date || isNaN(date.getTime())) {
    return "Invalid Date";
  }

  const dateString = date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const timeString = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return `${dateString} at ${timeString} (${timezone.name}, UTC${timezone.offset})`;
};

/**
 * Display a date in a specific timezone
 * This adjusts the display of the time based on the selected timezone
 */
export const displayDateInTimezone = (
  date: Date,
  timezoneOffset: string,
): string => {
  if (!date || isNaN(date.getTime())) {
    return "";
  }

  // Convert date to UTC
  const utcYear = date.getUTCFullYear();
  const utcMonth = date.getUTCMonth();
  const utcDate = date.getUTCDate();
  const utcHours = date.getUTCHours();
  const utcMinutes = date.getUTCMinutes();

  // Parse the timezone offset (e.g., "+01:00")
  const offsetSign = timezoneOffset.charAt(0) === "-" ? -1 : 1;
  const offsetHours = parseInt(timezoneOffset.substring(1, 3), 10);
  const offsetMinutes = parseInt(timezoneOffset.substring(4, 6), 10);

  // Adjust hours and minutes based on timezone offset
  let tzHours = utcHours + offsetSign * offsetHours;
  let tzMinutes = utcMinutes + offsetSign * offsetMinutes;

  // Handle minute overflow
  if (tzMinutes >= 60) {
    tzHours += 1;
    tzMinutes -= 60;
  } else if (tzMinutes < 0) {
    tzHours -= 1;
    tzMinutes += 60;
  }

  // Handle hour overflow
  let tzDate = utcDate;
  let tzMonth = utcMonth;
  let tzYear = utcYear;

  if (tzHours >= 24) {
    tzHours -= 24;
    tzDate += 1;
  } else if (tzHours < 0) {
    tzHours += 24;
    tzDate -= 1;
  }

  // Handle date overflow (simplified - doesn't account for different month lengths)
  const daysInMonth = new Date(tzYear, tzMonth + 1, 0).getDate();
  if (tzDate > daysInMonth) {
    tzDate = 1;
    tzMonth += 1;
  } else if (tzDate < 1) {
    tzMonth -= 1;
    if (tzMonth < 0) {
      tzMonth = 11;
      tzYear -= 1;
    }
    tzDate = new Date(tzYear, tzMonth + 1, 0).getDate();
  }

  // Handle month overflow
  if (tzMonth > 11) {
    tzMonth = 0;
    tzYear += 1;
  } else if (tzMonth < 0) {
    tzMonth = 11;
    tzYear -= 1;
  }

  // Create a new date object in the target timezone
  const tzDate2 = new Date(tzYear, tzMonth, tzDate, tzHours, tzMinutes, 0, 0);

  // Format the date string
  return tzDate2.toISOString();
};
